import React, { useState, useMemo, useEffect } from 'react';
import type { FillInTheBlankScreen as FillInTheBlankScreenType } from '../../types/course';
import { playCorrectSound, playIncorrectSound, playTickSound } from '../../utils/sound';

interface Props {
  screen: FillInTheBlankScreenType;
  onInteractionComplete: () => void;
  completed: boolean;
}

export const FillInTheBlankScreen: React.FC<Props> = ({
  screen,
  onInteractionComplete,
  completed,
}) => {
  const [filledBlanks, setFilledBlanks] = useState<(string | null)[]>(
    () => completed ? [...screen.answers] : screen.answers.map(() => null)
  );
  const [wrongSlot, setWrongSlot] = useState<number | null>(null);
  const [completedState, setCompletedState] = useState(completed);

  // Which blank are we filling next?
  const nextBlankIndex = filledBlanks.findIndex((b) => b === null);

  // All options shuffled, shown as chips
  const allOptions = useMemo(() => {
    const combined = [...screen.answers, ...screen.distractors];
    // Deterministic shuffle based on template
    const seed = screen.template.length;
    return combined.sort((a, b) => {
      const ha = (a.charCodeAt(0) * 31 + seed) % 100;
      const hb = (b.charCodeAt(0) * 31 + seed) % 100;
      return ha - hb;
    });
  }, [screen.answers, screen.distractors, screen.template]);

  // Which options have been used?
  const usedOptions = useMemo(() => new Set(filledBlanks.filter(Boolean)), [filledBlanks]);

  useEffect(() => {
    if (nextBlankIndex === -1 && !completedState) {
      setCompletedState(true);
      onInteractionComplete();
      playCorrectSound();
    }
  }, [nextBlankIndex, completedState, onInteractionComplete]);

  // Parse the template into segments
  const segments = useMemo(() => {
    const parts: { type: 'text' | 'blank'; value: string; index: number }[] = [];
    const regex = /\{\{(\d+)\}\}/g;
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(screen.template)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', value: screen.template.slice(lastIndex, match.index), index: -1 });
      }
      parts.push({ type: 'blank', value: '', index: parseInt(match[1]) });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < screen.template.length) {
      parts.push({ type: 'text', value: screen.template.slice(lastIndex), index: -1 });
    }
    return parts;
  }, [screen.template]);

  const handleOptionTap = (option: string) => {
    if (nextBlankIndex === -1 || usedOptions.has(option)) return;

    const correctAnswer = screen.answers[nextBlankIndex];
    if (option === correctAnswer) {
      playTickSound();
      setFilledBlanks((prev) => {
        const next = [...prev];
        next[nextBlankIndex] = option;
        return next;
      });
      setWrongSlot(null);
    } else {
      playIncorrectSound();
      setWrongSlot(nextBlankIndex);
      setTimeout(() => setWrongSlot(null), 600);
    }
  };

  return (
    <div className="flex flex-col gap-4 px-1">
      {screen.icon && <div className="text-3xl text-center">{screen.icon}</div>}
      <h3 className="text-lg font-semibold font-serif text-[var(--clue-text-primary)] text-center">
        {screen.title}
      </h3>

      {screen.body && (
        <>
          <p className="text-sm leading-relaxed text-[var(--clue-text-secondary)]">
            {screen.body}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-px bg-[var(--clue-border-mid)]" />
            <span className="text-[10px] font-semibold text-[var(--clue-text-tertiary)] uppercase tracking-widest">Your turn</span>
            <div className="flex-1 h-px bg-[var(--clue-border-mid)]" />
          </div>
        </>
      )}

      {/* Template with blanks */}
      <div className="rounded-lg bg-[var(--clue-bg-secondary)] border border-[var(--clue-border-mid)] p-4">
        <p className="text-sm leading-loose text-[var(--clue-text-secondary)]">
          {segments.map((seg, i) => {
            if (seg.type === 'text') {
              return <span key={i}>{seg.value}</span>;
            }
            const filled = filledBlanks[seg.index];
            const isNext = seg.index === nextBlankIndex;
            const isWrong = seg.index === wrongSlot;
            return (
              <span
                key={i}
                className={`inline-block min-w-[80px] mx-0.5 px-2 py-0.5 rounded border-b-2 text-center font-medium transition-all duration-200 ${
                  filled
                    ? 'border-[var(--clue-green-accent)] text-[var(--clue-green-accent)] bg-[var(--clue-green-accent)]/10'
                    : isWrong
                      ? 'border-red-400 bg-red-500/10 animate-shake'
                      : isNext
                        ? 'border-[var(--clue-green-accent)]/50 bg-[var(--clue-green-accent)]/5'
                        : 'border-[var(--clue-border-mid)] text-[var(--clue-text-tertiary)]'
                }`}
              >
                {filled || '___'}
              </span>
            );
          })}
        </p>
      </div>

      {/* Option chips */}
      {nextBlankIndex !== -1 && (
        <div className="flex flex-wrap gap-2 justify-center">
          {allOptions.map((option) => {
            const used = usedOptions.has(option);
            return (
              <button
                key={option}
                onClick={() => handleOptionTap(option)}
                disabled={used}
                className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-all duration-200 ${
                  used
                    ? 'opacity-30 border-[var(--clue-border-mid)] text-[var(--clue-text-tertiary)] cursor-not-allowed'
                    : 'border-[var(--clue-border-mid)] bg-[var(--clue-bg-primary)] text-[var(--clue-text-primary)] hover:border-[var(--clue-green-accent)] hover:bg-[var(--clue-green-accent)]/10 cursor-pointer'
                }`}
              >
                {option}
              </button>
            );
          })}
        </div>
      )}

      {completedState && !completed && (
        <div className="rounded-lg p-3 bg-[var(--clue-green-accent)]/10 border border-[var(--clue-green-accent)]/30 text-center">
          <p className="text-sm font-medium text-[var(--clue-green-accent)]">Perfect!</p>
        </div>
      )}
    </div>
  );
};
